import { FinModel } from "./finmodel.js";
import { GridModel } from "./gridmodel.js";
import { GameView } from "./gameview.js";
import { GameController } from "./gamecontroller.js";
import { ClueBoardsController } from "./clueboardscontroller.js";
import { Timer } from "./timer.js";

// FinGame object for central game state management (Singleton)
// Note: State management is now handled by StateMachine (window.stateMachine)

export class FinGame {
  constructor() {
    // this.finJsonData = null;
    this.finModel = null;
    this.gridModel = null;
    this.gameController = null;
    this.clueBoardsController = null;
    this.statusElement = null;
    this.versionElement = null;
    this.startButtonElement = null;
    this.timerButtonElement = null;
    this.puzzleName = "";
    this.puzzleSubfolder = ""; // Subfolder for PDF files (e.g., 'sample', 'archive')
    this.timer = null;
    this.debug = false;
    this.revealsUsed = false; // Track if any reveal menu options were used
  }

  /**
   * Initialize game view, controllers and timer for this FinGame instance.
   *
   * This method wires up the primary UI pieces and controllers needed to run the game:
   * - Creates a GameView using the provided canvas and stores it as this.gameView.
   * - Stores the provided status, start and timer button elements on the instance.
   * - Creates a ClueBoardsController using the provided clueBoardsArr.
   * - Binds a timer-update callback (this.updateTimerElement) to update the timer button text.
   * - Constructs a Timer with a 1000ms interval and the bound update callback (stored as this.timer).
   *
   * Side effects:
   * - Sets instance properties: gameView, statusElement, startButtonElement, timerButtonElement,
   *   clueBoardsController and timer.
   * - Registers this.updateTimerElement as an arrow function so it preserves lexical `this`.
   *
   * @function
   * @name setupGame
   * @memberof FinGame#
   * @param {Object} options - Configuration object for initializing the game.
   * @param {HTMLCanvasElement} options.canvas - Required. Canvas element used to create GameView. If omitted, an Error is thrown.
   * @param {HTMLElement} [options.statusElem] - Optional element used to display status messages.
   * @param {HTMLElement} [options.startButton] - Optional start button element (stored as startButtonElement).
   * @param {HTMLElement} [options.timerButton] - Optional timer button element; when present, the bound updateTimerElement
   *        will set its innerText to "Timer: " + theWatch.toString() each tick.
   * @param {Array<any>} [options.clueBoardsArr] - Optional array used to construct the ClueBoardsController (expected to contain
   *        clue board DOM nodes or configuration objects appropriate for ClueBoardsController).
   *
   * @throws {Error} If options.canvas is not provided.
   *
   * @returns {void}
   *
   * @example
   * const fg = new FinGame();
   * fg.setupGame({
   *   canvas: document.querySelector('#game-canvas'),
   *   statusElem: document.querySelector('#status'),
   *   startButton: document.querySelector('#start-btn'),
   *   timerButton: document.querySelector('#timer-btn'),
   *   clueBoardsArr: [/* clue board elements or configs *\/]
   * });
   */
  setupGame(options) {
    const gameCanvas = options.canvas;
    if (!gameCanvas) {
      throw new Error(
        "Game canvas is required for setupGame but was not provided.",
      );
    }

    // Create a new GameView instance with the canvas
    const myGameView = new GameView(gameCanvas);
    this.gameView = myGameView;

    this.statusElement = options.statusElem;
    this.startButtonElement = options.startButton;
    this.timerButtonElement = options.timerButton;
    const clueBoardsArr = options.clueBoardsArr;
    const cluesContainer = options.cluesContainer;
    this.clueBoardsController = new ClueBoardsController(
      clueBoardsArr,
      cluesContainer,
    );
    // Bind updateTimerElement as an arrow function to preserve 'this'
    this.updateTimerElement = (theWatch) => {
      if (this.timerButtonElement) {
        if (theWatch)
          this.timerButtonElement.innerText = "Timer: " + theWatch.toString();
      }
    };
    this.timer = new Timer(this.updateTimerElement, 1000);
  }

  /**
   * Load JSON string and use it to create models finModel and gridModel and a GameController.
   * This function does a lot of heavy lifting and should have better error checking and failure
   * recovery.
   * @param {string} finJson - JSON string holding the puzzle data and metadata
   */
  loadJsonData(finJson) {
    this.finModel = new FinModel(finJson.finmodel);
    this.puzzleName = String(finJson.metadata.puzzleName).trim();
    if (this.finModel) {
      if (typeof GridModel !== "undefined") {
        // Garbage collection in JavaScript is automatic, so when you assign a new value to this.gridModel,
        // the previous GridModel instance becomes eligible for garbage collection if there are no other references to it.
        this.gridModel = new GridModel(this.finModel);
      }
      if (this.gridModel) {
        if (this.gameView) {
          // Initialize the game controller with the models (pass 'this' for progress updates)
          this.gameController = new GameController(
            this.finModel,
            this.gameView,
            this.gridModel,
            this,
          );
          // Initialize the game controller
          this.gameController.init();
          // Set up the clue board controller and the clue boards
          this.clueBoardsController.buildClueBoards(
            this.finModel,
            this.gridModel,
          );
        }
      }
    }
  }

  playGame() {
    // State transitions now handled by StateMachine
    // This method just starts the game logic
    if (window.CLUEBOARD_DEBUG_LOG) console.log("Start the game!");
    // Reset reveals tracking when starting to play
    this.revealsUsed = false;
    if (this.gameController) this.gameController.doStart();
    // Timer is now managed by StateMachine state handlers

    // Tag Manager: PUSH a google tag event to the Data Layer
    window.dataLayer.push({
      event: "puzzle_play", // This is the Custom Event name for GTM Trigger
      puzzle_name: this.puzzleName, // This is a parameter GTM can read
    });
  }

  onPuzzleComplete() {
    console.log("FinGame: Puzzle complete!");

    // Tag Manager: PUSH a google tag event to the Data Layer
    window.dataLayer.push({
      event: "puzzle_complete", // This is the Custom Event name for GTM Trigger
    });
  }

  pauseGame() {
    if (this.debug) {
      if (typeof this.clueBoardsController.getAllClueBoards === "function") {
        const clueBoards = this.clueBoardsController.getAllClueBoards();
        clueBoards.forEach((board, i) => {
          if (typeof board.countUsedClueWords === "function") {
            console.log(
              `${i + 1}. ClueBoard, ${board.headerLabelText} used words:`,
              board.countUsedClueWords(),
            );
          }
        });
      }
    }

    if (this.areAllCluesUsed()) {
      console.log("All clues are used!");
    }
  }

  clearWord() {
    console.log("ClearWord: finGame");
    if (this.gameController) {
      this.gameController.doClearWord();
    }
  }

  solveWord() {
    console.log("SolveWord: FomGame");
    if (this.gameController) this.gameController.doSolveWord();
  }

  checkWord() {
    let isCorrect = false;
    console.log("CheckWord: FinGame", isCorrect);
    if (this.gameController) {
      isCorrect = this.gameController.doCheckWord();
    }
    return isCorrect;
  }

  clearTile() {
    console.log("ClearTile: FinGame");
    if (this.gameController) {
      this.gameController.doClearTile();
    }
  }

  solveTile() {
    console.log("solveTile: FinGame");
    if (this.gameController) {
      this.gameController.doSolveTile();
    }
    // Note: Progress update handled by GameController.doSolveTile()
  }

  checkTile() {
    let isCorrect = false;
    console.log("checkTile: FinGame");
    if (this.gameController) {
      isCorrect = this.gameController.doCheckTile();
    }
    return isCorrect;
  }

  clearModel() {
    console.log("[FINGAME] clearModel");
    this.resetGame();
    // Clear model
    this.finModel = null;
  }

  resetGame() {
    // Additional logic to reset the game can be added here
    console.log("[FINGAME]: Reset the game!");
    // Reset reveals tracking for new game
    this.revealsUsed = true;
    if (this.gameController) this.gameController.doReset();
    if (this.clueBoardsController) this.clueBoardsController.doReset();
    // Update progress after reset
    this.updateProgress();
    this.timer.stop();
    this.timer.reset();
    if (this.timerButtonElement) {
      this.timerButtonElement.innerText = "Timer: 00:00:00";
    }
  }

  solvePuzzle() {
    console.log("FinGame: Solve the game!");
    // Stop timer since puzzle is being revealed
    if (this.timer) this.timer.stop();
    // Mark that reveals were used
    this.revealsUsed = true;
    // Solve the puzzle
    if (this.gameController) this.gameController.doSolvePuzzle();
  }

  checkAllClues() {
    let allSolved = false;
    if (this.areAllCluesUsed()) {
      allSolved = true;
      // Trigger completion through StateMachine
      if (typeof this.onPuzzleComplete === "function") {
        this.onPuzzleComplete();
      }
    }
    return allSolved;
  }

  areAllCluesUsed() {
    if (!this.clueBoardsController) return false;
    // Assume each clue board has a method getUsedCount() and getTotalCount()
    let totalClues = this.finModel.getWordCount();
    let usedClues = 0;
    for (const board of this.clueBoardsController.clueBoards) {
      if (typeof board.countUsedClueWords === "function") {
        usedClues += board.countUsedClueWords();
      }
    }
    // console.log("Used clues:", usedClues);
    return totalClues > 0 && usedClues === totalClues;
  }

  /**
   * Handles clicks on clue list items from the clue boards.
   * @param {string} clueText - The text content of the clicked clue item (the word).
   * @param {HTMLElement} clueElement - The DOM element that was clicked.
   */
  handleClueItemClick(clueText, clueElement) {
    console.log("[FinGame] Clue item clicked:", {
      clueText: clueText,
      element: clueElement,
      isChecked: clueElement.classList.contains("checked"),
    });

    const isChecked = clueElement.classList.contains("checked");
    // const clueWord = this.clueBoardsController.getClueWordByValue(clueText);
    // const gridWord = clueWord.getGridWord();
    if (isChecked) {
      console.log("[FinGame] Found matching clue word.");
      this.gameController.handleClueWordClick(clueText);
    } else {
      console.warn(
        "[FinGame] No matching FULL grid word found for clue:",
        clueText,
      );
    }
  }

  /**
   * Update the progress indicator based on correctly filled tiles.
   * Called whenever tiles are modified.
   */
  updateProgress() {
    if (!this.gridModel) return;

    const progress = this.gridModel.calculateProgress();
    const progressElem = document.querySelector("#game-progress");

    if (progressElem) {
      progressElem.textContent = `Progress: ${progress.percentage}% (${progress.filledTiles}/${progress.totalTiles})`;
    }

    return progress;
  }

  setResponsiveMode(mode) {
    console.log(`[FinGame] setResponsiveMode: ${mode}`);
    this.responsiveMode = mode;

    // Propagate to game controller/view for canvas rendering
    if (this.gameController?.setResponsiveMode) {
      this.gameController.setResponsiveMode(mode);
    }
  }

  setCompactMode(isCompact) {
    console.log(`[FinGame] setCompactMode: ${isCompact}`);
    this.compactMode = isCompact;

    // Adjust UI elements outside the canvas
    const clueBoards = document.querySelectorAll(".clues-section");
    const infoArea = document.querySelector(".info-area");
    const cluesArea = document.querySelector(".clues-area");

    if (isCompact) {
      // mobile: Show all clue boards (including dynamically created ones)
      clueBoards.forEach((board) => {
        board.style.display = "block"; // Explicitly set to block to override CSS
      });

      // Reduce padding
      if (cluesArea) {
        cluesArea.style.padding = "12px";
      }

      // Compress info area
      if (infoArea) {
        infoArea.style.minHeight = "100px";
        infoArea.style.gap = "8px";
      }

      console.log(
        `[FinGame] Compact mode: showing all ${clueBoards.length} clue boards`,
      );
    } else {
      // Tablet/Desktop: Show all clue boards (including dynamically created ones)
      clueBoards.forEach((board) => {
        board.style.display = "block"; // Explicitly set to block
      });

      // Restore normal padding
      if (cluesArea) {
        cluesArea.style.padding = "";
      }

      // Restore normal info area
      if (infoArea) {
        infoArea.style.minHeight = "";
        infoArea.style.gap = "";
      }

      console.log(
        `[FinGame] Full mode: showing all ${clueBoards.length} clue boards`,
      );
    }
  }

  /**
   * Print a PDF of the puzzle or solution.
   * @param {string} mode - Either 'puzzle' or 'solution'
   * @returns {string|null} - The URL of the PDF or null if failed
   */
  printPDF(mode = "puzzle") {
    console.log(`[FinGame] printPDF called with mode: ${mode}`);

    // Validate mode parameter
    if (mode !== "puzzle" && mode !== "solution") {
      console.error(
        `[FinGame] printPDF: Invalid mode '${mode}'. Must be 'puzzle' or 'solution'.`,
      );
      return null;
    }

    // Determine puzzle name (prefer explicit property, fallback to finModel metadata)
    const rawName =
      (this.puzzleName && String(this.puzzleName).trim()) ||
      (this.finModel?.metadata?.puzzleName &&
        String(this.finModel.metadata.puzzleName).trim()) ||
      "";

    if (!rawName) {
      console.error("[FinGame] printPDF: no puzzle name available");
      alert("Cannot print: puzzle name not found");
      return null;
    }

    console.log("[FinGame] Puzzle name:", rawName);

    // Construct filename based on mode
    const fileName =
      mode === "solution" ? `${rawName}_sol.pdf` : `${rawName}.pdf`;

    console.log("[FinGame] PDF filename:", fileName);

    // Resolve data folder from environment variable or default to '/data'
    let dataFolder = "/data";

    // Check Vite environment variable
    if (import.meta.env?.VITE_DATA_FOLDER) {
      dataFolder = import.meta.env.VITE_DATA_FOLDER;
      console.log("[FinGame] Using VITE_DATA_FOLDER:", dataFolder);
    }
    // Check for global config fallback
    else if (typeof window !== "undefined" && window.APP_CONFIG?.DATA_FOLDER) {
      dataFolder = window.APP_CONFIG.DATA_FOLDER;
      console.log("[FinGame] Using APP_CONFIG.DATA_FOLDER:", dataFolder);
    } else {
      console.log("[FinGame] Using default data folder:", dataFolder);
    }

    // Normalize path and build full URL with subfolder support
    dataFolder = String(dataFolder).replace(/\/+$/, ""); // Remove trailing slashes

    // Incorporate subfolder if specified (e.g., 'sample', 'archive')
    let pdfPath = dataFolder;
    if (this.puzzleSubfolder) {
      pdfPath = `${dataFolder}/${this.puzzleSubfolder}`;
      console.log("[FinGame] Using subfolder:", this.puzzleSubfolder);
    }

    const url = `${pdfPath}/${encodeURIComponent(fileName)}`;

    console.log("[FinGame] Opening PDF:", url);

    // Attempt to open PDF in a new tab/window
    const newWin = window.open(url, "_blank");

    if (!newWin || newWin.closed || typeof newWin.closed === "undefined") {
      // Popup blocked - show user message
      console.warn("[FinGame] Popup blocked, prompting user");
      const pdfType = mode === "solution" ? "solution PDF" : "puzzle PDF";
      if (
        confirm(
          `Popup blocked. Click OK to navigate to the ${pdfType}:\n${url}`,
        )
      ) {
        window.location.href = url;
      }
    } else {
      console.log("[FinGame] PDF opened in new window");
    }

    return url;
  }

  /**
   * Print the puzzle PDF.
   * @returns {string|null} - The URL of the PDF or null if failed
   */
  printPuzzle() {
    return this.printPDF("puzzle");
  }

  /**
   * Print the solution PDF.
   * @returns {string|null} - The URL of the PDF or null if failed
   */
  printSolution() {
    return this.printPDF("solution");
  }
}

export default FinGame;
